import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnDestroy,
  inject,
  HostListener
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { QuillModule } from 'ngx-quill';
import {
  CoinsingReleaseService,
  CoinSingReleaseOutcome
} from '../../services/coinsing-release.service';
import {
  compareVersions,
  normalizeInstalledVersion,
  VersionCatalogEntry
} from '../../utils/coinsing-version.util';
import {
  computeCoinSingUpdatePill,
  CoinSingPillResult
} from '../../utils/coinsing-update-pill.util';

@Component({
  selector: 'app-konobox-device-release-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, QuillModule],
  templateUrl: './konobox-device-release-modal.component.html',
  styleUrls: ['./konobox-device-release-modal.component.css']
})
export class KonoboxDeviceReleaseModalComponent implements OnInit, OnDestroy {
  @Input() deviceId!: string;
  @Input() deviceName!: string;
  @Input() isDeviceOnline: boolean = true;
  @Input() currentAppVersion?: string;
  @Input() canEditNotes: boolean = false;

  @Output() close = new EventEmitter<void>();
  @Output() released = new EventEmitter<CoinSingReleaseOutcome>();

  private firestore = inject(Firestore);
  private releaseService = inject(CoinsingReleaseService);
  private sanitizer = inject(DomSanitizer);

  isLoading = true;
  isPublishing = false;
  isRefreshingStatus = false;
  errorMessage = '';
  statusMessage = '';

  appVersionsList: (VersionCatalogEntry & { isRollback?: boolean })[] = [];
  selectedVersion: (VersionCatalogEntry & { isRollback?: boolean }) | null = null;
  forceUpdate = false;

  // Live device release tracking
  deviceTargetVersion?: string;
  deviceUpdateNeeded = false;
  deviceUpdateState?: any;
  updatePill: CoinSingPillResult = {
    pillText: null,
    pillClass: '',
    ariaLabel: '',
    isRollback: false
  };

  // Rollback confirmation dialog state
  showRollbackConfirmDialog = false;

  // Release Note Drawer State
  showReleaseNoteDrawer = false;
  isLoadingNote = false;
  isSavingNote = false;
  isEditingNote = false;
  releaseNoteContent = '';
  releaseNoteUpdatedAt?: string;
  releaseNoteUpdatedBy?: string;
  noteFeedbackMessage = '';
  noteErrorMessage = '';

  quillConfig = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ header: 1 }, { header: 2 }, { header: 3 }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ color: [] }, { background: [] }],
      [{ align: [] }],
      ['clean'],
      ['link']
    ]
  };

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent): void {
    if (this.isPublishing || this.isSavingNote) return;
    if (this.showRollbackConfirmDialog) {
      this.cancelRollbackDialog();
      event.stopPropagation();
      return;
    }
    if (this.showReleaseNoteDrawer) {
      this.closeReleaseNotesDrawer();
      event.stopPropagation();
      return;
    }
    this.closeModal();
  }

  async ngOnInit(): Promise<void> {
    await this.loadAll();
  }

  ngOnDestroy(): void {}

  closeModal(): void {
    if (this.isPublishing) return;
    this.close.emit();
  }

  async loadAll(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = '';
    try {
      // 1. Load catalog versions
      const rawVersions = await this.releaseService.getAppReleases();
      // Only valid versions can be targeted to a device
      const validVersions = rawVersions.filter(v => v.isValid);

      // 2. Load device live snapshot
      await this.refreshDeviceData();

      // 3. Mark rollback status on each version relative to currently installed version
      const installedNorm = normalizeInstalledVersion(this.currentAppVersion);
      this.appVersionsList = validVersions.map(v => {
        let isRollback = false;
        if (installedNorm && v.version) {
          try {
            isRollback = compareVersions(v.version, installedNorm) < 0;
          } catch {
            isRollback = false;
          }
        }
        return {
          ...v,
          isRollback
        };
      });

      if (this.appVersionsList.length > 0) {
        this.selectedVersion = this.appVersionsList[0];
      }
    } catch (err: any) {
      console.error('Error loading release modal data:', err);
      this.errorMessage = 'Failed to load app releases or device data.';
    } finally {
      this.isLoading = false;
    }
  }

  async refreshDeviceData(): Promise<void> {
    if (!this.deviceId) return;
    this.isRefreshingStatus = true;
    try {
      const snap = await getDoc(doc(this.firestore, 'devices', this.deviceId));
      if (snap.exists()) {
        const data = snap.data();
        if (data['appVersion'] || data['currentAppVersion']) {
          this.currentAppVersion = data['appVersion'] || data['currentAppVersion'];
        }
        this.deviceTargetVersion = data['coinsingTargetVersion'];
        this.deviceUpdateNeeded = data['coinsingUpdateNeeded'] === true;
        this.deviceUpdateState = data['coinsingUpdate'];
        this.isDeviceOnline = data['deviceOnline'] !== false && data['isOnline'] !== false;

        this.updatePill = computeCoinSingUpdatePill({
          appVersion: this.currentAppVersion,
          coinsingTargetVersion: this.deviceTargetVersion,
          coinsingUpdateNeeded: this.deviceUpdateNeeded,
          coinsingUpdate: this.deviceUpdateState
        });
      }
    } catch (err) {
      console.error('Error refreshing device status:', err);
    } finally {
      this.isRefreshingStatus = false;
    }
  }

  get installedVersionDisplay(): string {
    const norm = normalizeInstalledVersion(this.currentAppVersion);
    return norm ? `v${norm}` : 'Unknown';
  }

  get isSelectedVersionRollback(): boolean {
    if (!this.selectedVersion?.version) return false;
    const installedNorm = normalizeInstalledVersion(this.currentAppVersion);
    if (!installedNorm) return false;
    try {
      return compareVersions(this.selectedVersion.version, installedNorm) < 0;
    } catch {
      return false;
    }
  }

  get isSelectedVersionSameAsInstalled(): boolean {
    if (!this.selectedVersion?.version) return false;
    const installedNorm = normalizeInstalledVersion(this.currentAppVersion);
    return installedNorm === this.selectedVersion.version;
  }

  get directionText(): 'Upgrade' | 'Reinstall' | 'Rollback' {
    if (this.isSelectedVersionSameAsInstalled) return 'Reinstall';
    if (this.isSelectedVersionRollback) return 'Rollback';
    return 'Upgrade';
  }

  get isReleaseDisabled(): boolean {
    if (!this.selectedVersion || this.isLoading || this.isPublishing) return true;
    if (this.isSelectedVersionSameAsInstalled && !this.forceUpdate) return true;
    return false;
  }

  get sameVersionHint(): string {
    if (this.isSelectedVersionSameAsInstalled && !this.forceUpdate && this.selectedVersion) {
      return `v${this.selectedVersion.version} is already installed. Tick Force to reinstall.`;
    }
    return '';
  }

  get selectedBuildConfiguration(): string | null {
    return this.selectedVersion?.buildConfiguration || null;
  }

  get selectedGitCommitShort(): string | null {
    const commit = this.selectedVersion?.gitCommit;
    return commit ? commit.substring(0, 8) : null;
  }

  onSelectVersion(event: Event): void {
    const versionStr = (event.target as HTMLSelectElement).value;
    this.selectedVersion = this.appVersionsList.find(v => v.version === versionStr) || null;
    if (this.showReleaseNoteDrawer && this.selectedVersion) {
      void this.loadReleaseNoteForSelectedVersion();
    }
  }

  onReleaseClick(): void {
    if (this.isReleaseDisabled || !this.selectedVersion) return;

    if (this.isSelectedVersionRollback) {
      this.showRollbackConfirmDialog = true;
      setTimeout(() => document.getElementById('btnCancelRollback')?.focus(), 0);
      return;
    }

    void this.executeRelease(false);
  }

  confirmRollback(): void {
    this.showRollbackConfirmDialog = false;
    void this.executeRelease(true);
  }

  cancelRollbackDialog(): void {
    this.showRollbackConfirmDialog = false;
    setTimeout(() => document.getElementById('btnReleaseToDevice')?.focus(), 0);
  }

  async executeRelease(rollbackConfirmed: boolean): Promise<void> {
    if (!this.selectedVersion || this.isPublishing) return;

    this.isPublishing = true;
    this.statusMessage = '';
    this.errorMessage = '';

    const targetVersion = this.selectedVersion.version;

    try {
      const outcome = await this.releaseService.releaseAppToDevice(
        this.deviceId,
        targetVersion,
        this.forceUpdate,
        rollbackConfirmed
      );

      // AC-2 string constant:
      this.statusMessage = `v${targetVersion} is set as the target for ${this.deviceName || this.deviceId}. It will install at the next restart.`;
      this.deviceTargetVersion = targetVersion;
      this.deviceUpdateNeeded = true;

      await this.refreshDeviceData();
      this.released.emit(outcome);
    } catch (err: any) {
      console.error('Failed to release CoinSing to device:', err);
      if (err.message?.includes('rollback_confirmation_required')) {
        this.showRollbackConfirmDialog = true;
      } else {
        this.errorMessage = err.message || 'Error releasing app to device.';
      }
    } finally {
      this.isPublishing = false;
    }
  }

  // ---------------------------------------------------------------------------
  // Release Notes Drawer Logic (AC-7)
  // ---------------------------------------------------------------------------
  async toggleReleaseNotesDrawer(openState?: boolean): Promise<void> {
    this.showReleaseNoteDrawer = openState !== undefined ? openState : !this.showReleaseNoteDrawer;
    if (this.showReleaseNoteDrawer && this.selectedVersion) {
      await this.loadReleaseNoteForSelectedVersion();
    }
  }

  closeReleaseNotesDrawer(): void {
    this.showReleaseNoteDrawer = false;
    this.isEditingNote = false;
    this.noteFeedbackMessage = '';
    this.noteErrorMessage = '';
  }

  async loadReleaseNoteForSelectedVersion(): Promise<void> {
    if (!this.selectedVersion?.version) return;

    this.isLoadingNote = true;
    this.isEditingNote = false;
    this.noteFeedbackMessage = '';
    this.noteErrorMessage = '';

    try {
      const verData = await this.releaseService.getAppReleaseVersion(this.selectedVersion.version);
      if (verData) {
        this.releaseNoteContent = verData.releaseNote || '';
        this.releaseNoteUpdatedAt = verData.releaseNoteUpdatedAt;
        this.releaseNoteUpdatedBy = verData.releaseNoteUpdatedBy;
      } else {
        this.releaseNoteContent = '';
        this.releaseNoteUpdatedAt = undefined;
        this.releaseNoteUpdatedBy = undefined;
      }
    } catch (err: any) {
      console.error('Error loading release note:', err);
      this.noteErrorMessage = 'Failed to load release notes for this version.';
    } finally {
      this.isLoadingNote = false;
    }
  }

  startEditNote(): void {
    if (!this.canEditNotes) return;
    this.isEditingNote = true;
    this.noteFeedbackMessage = '';
    this.noteErrorMessage = '';
  }

  cancelEditNote(): void {
    this.isEditingNote = false;
    this.noteFeedbackMessage = '';
    this.noteErrorMessage = '';
    void this.loadReleaseNoteForSelectedVersion();
  }

  async saveReleaseNote(): Promise<void> {
    if (!this.selectedVersion?.version || !this.canEditNotes) return;

    this.isSavingNote = true;
    this.noteFeedbackMessage = '';
    this.noteErrorMessage = '';

    try {
      await this.releaseService.saveAppReleaseNote(
        this.selectedVersion.version,
        this.releaseNoteContent || ''
      );
      this.releaseNoteUpdatedAt = new Date().toISOString();
      this.noteFeedbackMessage = 'Release note saved successfully!';
      this.isEditingNote = false;
    } catch (err: any) {
      console.error('Error saving release note:', err);
      this.noteErrorMessage = `Failed to save release note: ${err.message || err}`;
    } finally {
      this.isSavingNote = false;
    }
  }

  /**
   * Sanitizes release notes safely without ever using bypassSecurityTrustHtml (AC-7).
   */
  getSanitizedNote(): SafeHtml {
    // Angular built-in safe HTML sanitizer removes dangerous script/onerror handlers while preserving tags
    return this.releaseNoteContent || '';
  }
}

